import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listClasses, listSubjects, listStaff, listSubjectCombinations, getPeriodRulesDefault, TEACHER_DEPARTMENT, getPccmDraft, setPccmDraft, applyPccmDraft } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Trang "Soạn thảo PCCM": bảng nháp Lớp × Bộ môn để thử phân công giáo viên,
// HOÀN TOÀN ĐỘC LẬP với classSubjects (dữ liệu PCCM thật đang dùng để xếp TKB).
// Sửa/lưu ở đây không ảnh hưởng gì tới xếp thời khoá biểu.
const EXCLUDE = ["pháp luật", "lập trình thiết kế web"];

const HappySchoolPccmDraft = () => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [combos, setCombos] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rules, setRules] = useState({});
  const [draft, setDraft] = useState({});
  const [tab, setTab] = useState("matrix"); // "matrix" | "teacher"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const reload = () => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    Promise.all([listClasses(schoolId), listSubjects(schoolId), listStaff(schoolId, { department: TEACHER_DEPARTMENT }), listSubjectCombinations(schoolId), getPeriodRulesDefault(schoolId), getPccmDraft(schoolId)])
      .then(([cls, subs, sf, cb, pr, d]) => {
        const sortedClasses = (cls || []).slice().sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true }));
        setClasses(sortedClasses);
        setSubjects((subs || []).filter((s) => !EXCLUDE.includes(String(s.name).toLowerCase().trim())).sort((a, b) => a.name.localeCompare(b.name, "vi")));
        setStaff(sf || []);
        setCombos(cb || []);
        setRules(pr || {});
        // Hiện đúng dữ liệu đã lưu trong bản nháp — không tự điền/gợi ý từ
        // PCCM thật (classSubjects) để tránh nhầm lẫn với dữ liệu Excel.
        setDraft(JSON.parse(JSON.stringify(d || {})));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [schoolId]); // eslint-disable-line

  const comboById = useMemo(() => Object.fromEntries(combos.map((c) => [c.id, c])), [combos]);
  // Môn có thuộc tổ hợp của lớp không — Tiếng anh luôn học chung, không nằm
  // trong danh sách môn của tổ hợp nên luôn cho phép riêng.
  const subjectAllowed = (cls, subject) => {
    if (String(subject.name).toLowerCase().trim() === "tiếng anh") return true;
    const combo = comboById[cls.combinationId];
    if (!combo) return true; // chưa gán tổ hợp -> không biết, cho chọn hết
    return (combo.subjectIds || []).includes(subject.id);
  };

  const staffBySubject = useMemo(() => {
    const map = {};
    subjects.forEach((s) => {
      map[s.id] = staff
        .filter((t) => (t.subjectIds || []).includes(s.id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, "vi"));
    });
    return map;
  }, [subjects, staff]);

  const cellVal = (classId, subjectId) => (draft[classId] || {})[subjectId] || "";
  const setCell = (classId, subjectId, staffId) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const c = (next[classId] = next[classId] || {});
      if (staffId) c[subjectId] = staffId;
      else delete c[subjectId];
      return next;
    });
    setMsg("");
  };

  // Số tiết/tuần của 1 (lớp, môn) — lấy từ bảng "Số tiết" (Học kỳ 1) theo
  // Loại lớp (Chọn/Thường) × Khối, y hệt logic trang "Số tiết".
  const periodsFor = (cls, subjectId) => {
    const loaiKey = cls.classType === "LỚP CHỌN" ? "chon" : "thuong";
    const khoi = (String(cls.name).match(/^(\d{1,2})A/) || [])[1];
    const cell = (((rules.hk1 || {})[loaiKey] || {})[khoi] || {})[subjectId];
    if (!cell) return 0;
    return (Number(cell.ck) || 0) + (Number(cell.tc) || 0) + (Number(cell.cd) || 0);
  };

  // Nhóm theo GV — giống bố cục sheet "PCCM": Bộ môn, Họ tên GV, các lớp và
  // số tiết đang dạy theo từng khối, tổng số lớp/tổng số tiết.
  const teacherReport = useMemo(() => {
    const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
    const subjectById = Object.fromEntries(subjects.map((s) => [s.id, s]));
    const byTeacher = {};
    Object.entries(draft).forEach(([classId, subjMap]) => {
      const cls = classById[classId];
      if (!cls) return;
      const khoi = (String(cls.name).match(/^(\d{1,2})A/) || [])[1] || "?";
      Object.entries(subjMap).forEach(([subjId, staffId]) => {
        if (!staffId) return;
        const t = staff.find((s) => s.id === staffId);
        const subj = subjectById[subjId];
        if (!t) return;
        const rec = (byTeacher[staffId] = byTeacher[staffId] || {
          name: t.name,
          employmentType: t.employmentType,
          dobYear: (String(t.dob || "").match(/^(\d{4})-/) || [])[1] || "",
          subjectNames: new Set(),
          subjectOrder: t.subjectIds || [],
          khoi: {},
          tietKhoi: {},
        });
        rec.subjectNames.add(subj ? subj.name : subjId);
        rec.khoi[khoi] = rec.khoi[khoi] || [];
        if (!rec.khoi[khoi].includes(cls.name)) rec.khoi[khoi].push(cls.name);
        rec.tietKhoi[khoi] = (rec.tietKhoi[khoi] || 0) + periodsFor(cls, subjId);
      });
    });
    // Chủ nhiệm — không phụ thuộc bảng nháp, quét thẳng classes.homeroomTeacherId
    // để gắn vào đúng GV (chỉ gắn cho GV đã có mặt trong báo cáo ở trên).
    classes.forEach((cls) => {
      if (!cls.homeroomTeacherId) return;
      const rec = byTeacher[cls.homeroomTeacherId];
      if (!rec) return;
      const khoi = (String(cls.name).match(/^(\d{1,2})A/) || [])[1] || "?";
      rec.chuNhiem = rec.chuNhiem || {};
      rec.chuNhiem[khoi] = rec.chuNhiem[khoi] || [];
      if (!rec.chuNhiem[khoi].includes(cls.name)) rec.chuNhiem[khoi].push(cls.name);
    });
    const clsSortKey = (n) => { const m = String(n).match(/A(\d+)/); return m ? Number(m[1]) : 99; };
    return Object.values(byTeacher)
      .map((r) => {
        Object.keys(r.khoi).forEach((k) => r.khoi[k].sort((a, b) => clsSortKey(a) - clsSortKey(b)));
        const tongLop = Object.values(r.khoi).reduce((s, arr) => s + arr.length, 0);
        const tongTiet = Object.values(r.tietKhoi).reduce((s, n) => s + n, 0);
        // Sắp theo đúng thứ tự subjectIds trong hồ sơ GV, môn nào không có trong đó thì thêm cuối.
        const ordered = r.subjectOrder.map((sid) => (subjectById[sid] || {}).name).filter((n) => n && r.subjectNames.has(n));
        [...r.subjectNames].forEach((n) => { if (!ordered.includes(n)) ordered.push(n); });
        return { ...r, subjectNames: ordered.join(", "), tongLop, tongTiet };
      })
      .sort((a, b) => a.subjectNames.localeCompare(b.subjectNames, "vi") || a.name.localeCompare(b.name, "vi"));
  }, [draft, classes, subjects, staff, rules]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    setError("");
    setMsg("");
    try {
      await setPccmDraft(schoolId, draft);
      setMsg("Đã lưu bảng soạn thảo PCCM (nháp).");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Duyệt: ghi đè GV từ bản nháp vào classSubjects (PCCM thật, dùng để xếp
  // TKB) — (lớp,môn) đã có sẵn thì chỉ đổi GV, chưa có thì server tự tính
  // số tiết/tuần từ bảng "Số tiết"; không tính được thì server báo lại (skipped).
  const applyDraft = async () => {
    if (!window.confirm("Ghi đè giáo viên từ bản nháp vào PCCM THẬT (đang dùng để xếp thời khoá biểu)?\nHành động này ảnh hưởng trực tiếp tới việc xếp TKB, không thể hoàn tác tự động.")) return;
    setApplying(true);
    setError("");
    setMsg("");
    try {
      const res = await applyPccmDraft(schoolId);
      let text = `Đã duyệt ${res.applied} ô vào PCCM thật.`;
      if (res.skipped && res.skipped.length) {
        text += ` Bỏ qua ${res.skipped.length} ô chưa có số tiết/tuần (chưa có quy định "Số tiết" cho môn đó ở khối này): ` +
          res.skipped.map((s) => `${s.className}-${(subjects.find((x) => x.id === s.subjectId) || {}).name || s.subjectId}`).join(", ");
      }
      setMsg(text);
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  };

  if (!schoolId) return <div style={{ padding: 16 }}>Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link>.</div>;
  if (loading) return <div style={{ padding: 8 }}>Đang tải...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1300, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>📝 Soạn thảo PCCM (nháp)</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Bảng thử phân công GV theo Lớp × Bộ môn. Sửa/bấm "Lưu" ở đây chỉ lưu vào bản nháp, <b>độc lập</b> với PCCM thật đang dùng để xếp TKB — chỉ khi bấm <b>"Duyệt"</b> mới ghi GV vào PCCM thật.
      </p>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setTab("matrix")} style={tab === "matrix" ? tabActive : tabInactive}>Ma trận Lớp × Môn</button>
          <button onClick={() => setTab("teacher")} style={tab === "teacher" ? tabActive : tabInactive}>Theo Giáo viên</button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={save} disabled={saving} style={primary}>{saving ? "Đang lưu..." : "💾 Lưu"}</button>
          <button onClick={applyDraft} disabled={applying} style={dangerBtn}>{applying ? "Đang duyệt..." : "✅ Duyệt vào PCCM thật"}</button>
        </div>
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {msg && <p style={{ color: "#059669", fontSize: 13 }}>{msg}</p>}

      {tab === "matrix" ? (
        <>
          <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 13, background: "#fff", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...thFix(0), ...clip, top: 0, textAlign: "left", width: MCOL.wLop }}>Lớp</th>
                  <th style={{ ...th, ...thFix(MCOL.toHop), ...clip, top: 0, textAlign: "left", width: MCOL.wToHop }}>Tổ hợp</th>
                  {subjects.map((s) => (<th key={s.id} style={{ ...th, top: 0, position: "sticky", zIndex: 3, minWidth: 160 }}>{s.name}</th>))}
                </tr>
              </thead>
              <tbody>
                {classes.map((c) => {
                  const disbanded = c.status === "giai_tan";
                  return (
                  <tr key={c.id} style={disbanded ? { opacity: 0.6 } : undefined}>
                    <td style={{ ...td, ...tdFix(0), ...clip, width: MCOL.wLop, textAlign: "left", fontWeight: 700 }} title={c.name}>
                      {c.name}
                      {disbanded && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: "#dc2626" }}>GIẢI TÁN</span>}
                    </td>
                    <td style={{ ...td, ...tdFix(MCOL.toHop), ...clip, width: MCOL.wToHop, textAlign: "left", fontSize: 12, color: "#4b5563" }} title={(comboById[c.combinationId] || {}).name || "—"}>{(comboById[c.combinationId] || {}).name || "—"}</td>
                    {subjects.map((s) => {
                      const options = staffBySubject[s.id] || [];
                      const allowed = subjectAllowed(c, s);
                      if (disbanded) {
                        return (
                          <td key={s.id} style={{ ...td, background: "#f3f4f6" }}>
                            <div style={{ fontSize: 10, color: "#9ca3af" }}>Lớp đã giải tán</div>
                          </td>
                        );
                      }
                      if (!allowed) {
                        return (
                          <td key={s.id} style={{ ...td, background: "#f3f4f6" }}>
                            <div style={{ fontSize: 10, color: "#9ca3af" }}>Lớp không học môn này</div>
                          </td>
                        );
                      }
                      return (
                        <td key={s.id} style={td}>
                          <select
                            value={cellVal(c.id, s.id)}
                            onChange={(e) => setCell(c.id, s.id, e.target.value)}
                            style={sel}
                          >
                            <option value="">— Chọn GV —</option>
                            {options.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                          </select>
                          {options.length === 0 && <div style={{ fontSize: 10, color: "#f59e0b" }}>Chưa có GV môn này</div>}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>Danh sách GV mỗi cột chỉ hiện đúng GV có khai môn tương ứng (ở trang Nhân sự). Nhớ bấm "Lưu" sau khi chọn.</p>
        </>
      ) : (
        <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, background: "#fff", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, ...thFix(TCOL.stt), ...clip, top: 0, zIndex: 5, width: TCOL.wStt }}>STT</th>
                <th style={{ ...th, ...thFix(TCOL.bomon), ...clip, top: 0, zIndex: 5, textAlign: "left", width: TCOL.wBomon }}>Bộ môn</th>
                <th style={{ ...th, ...thFix(TCOL.ten), ...clip, top: 0, zIndex: 5, textAlign: "left", width: TCOL.wTen }}>Họ và tên GV</th>
                <th style={{ ...th, ...thFix(TCOL.namsinh), ...clip, top: 0, zIndex: 5, width: TCOL.wNamsinh }}>Năm sinh</th>
                <th style={{ ...th, ...thFix(TCOL.loai), ...clip, top: 0, zIndex: 5, width: TCOL.wLoai }}>Loại</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, textAlign: "left", minWidth: 200 }}>Khối 12</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Số tiết K12</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, textAlign: "left", minWidth: 200 }}>Khối 11</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Số tiết K11</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, textAlign: "left", minWidth: 200 }}>Khối 10</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Số tiết K10</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Tổng số lớp</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3, background: "#ecfdf5" }}>Tổng số tiết</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Chủ nhiệm lớp 12</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Chủ nhiệm lớp 11</th>
                <th style={{ ...th, top: 0, position: "sticky", zIndex: 3 }}>Chủ nhiệm 10</th>
              </tr>
            </thead>
            <tbody>
              {teacherReport.map((r, i) => (
                <tr key={r.name + i}>
                  <td style={{ ...td, ...tdFix(TCOL.stt), ...clip, width: TCOL.wStt }}>{i + 1}</td>
                  <td style={{ ...td, ...tdFix(TCOL.bomon), ...clip, width: TCOL.wBomon, textAlign: "left" }} title={r.subjectNames}>{r.subjectNames}</td>
                  <td style={{ ...td, ...tdFix(TCOL.ten), ...clip, width: TCOL.wTen, textAlign: "left", fontWeight: 700 }} title={r.name}>{r.name}</td>
                  <td style={{ ...td, ...tdFix(TCOL.namsinh), ...clip, width: TCOL.wNamsinh }}>{r.dobYear}</td>
                  <td style={{ ...td, ...tdFix(TCOL.loai), ...clip, width: TCOL.wLoai }} title={r.employmentType || ""}>{r.employmentType || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{(r.khoi["12"] || []).join(" + ")}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tietKhoi["12"] || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{(r.khoi["11"] || []).join(" + ")}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tietKhoi["11"] || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{(r.khoi["10"] || []).join(" + ")}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tietKhoi["10"] || ""}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.tongLop}</td>
                  <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{r.tongTiet}</td>
                  <td style={td}>{((r.chuNhiem || {})["12"] || []).join(" + ")}</td>
                  <td style={td}>{((r.chuNhiem || {})["11"] || []).join(" + ")}</td>
                  <td style={td}>{((r.chuNhiem || {})["10"] || []).join(" + ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const primary = { padding: "7px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 };
const dangerBtn = { padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 };
const tabInactive = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabActive = { ...tabInactive, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap" };
const thFix = (left) => ({ position: "sticky", left, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "4px 6px", textAlign: "center" };
const tdFix = (left) => ({ position: "sticky", left, zIndex: 1, background: "#fff" });

// Ép cột luôn đúng độ rộng đã định (cắt bớt chữ + "..." nếu dài hơn) — nếu
// không, nội dung dài (vd danh sách nhiều môn ở cột Bộ môn) sẽ đẩy cột rộng
// ra thực tế, làm lệch vị trí "left" các cột ghim phía sau, đè lên cột Khối 12.
const clip = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", boxSizing: "border-box" };

// Vị trí + độ rộng 5 cột cố định (STT, Bộ môn, Họ và tên GV, Năm sinh, Loại)
// ở tab "Theo Giáo viên" — cộng dồn để cột sau luôn nằm sát cột trước.
const TCOL_W = { stt: 44, bomon: 130, ten: 180, namsinh: 80, loai: 100 };
const TCOL = {
  wStt: TCOL_W.stt, wBomon: TCOL_W.bomon, wTen: TCOL_W.ten, wNamsinh: TCOL_W.namsinh, wLoai: TCOL_W.loai,
  stt: 0,
  bomon: TCOL_W.stt,
  ten: TCOL_W.stt + TCOL_W.bomon,
  namsinh: TCOL_W.stt + TCOL_W.bomon + TCOL_W.ten,
  loai: TCOL_W.stt + TCOL_W.bomon + TCOL_W.ten + TCOL_W.namsinh,
};
const sel = { width: "100%", padding: "5px 4px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: 12 };

// Vị trí + độ rộng 2 cột cố định (Lớp, Tổ hợp) ở tab "Ma trận Lớp × Môn" —
// giống hệt cơ chế TCOL bên trên, tránh lặp lại lỗi cột bị đè/hở khe.
const MCOL_W = { lop: 90, toHop: 130 };
const MCOL = { wLop: MCOL_W.lop, wToHop: MCOL_W.toHop, lop: 0, toHop: MCOL_W.lop };

export default HappySchoolPccmDraft;
